import { parseProvisioningFlags } from "./provisioningFlags";
import type { ToolConfiguration, BuildToolConfigParams } from "./types";

export function buildToolConfiguration(params: BuildToolConfigParams): ToolConfiguration[] {
  const {
    arenaName,
    domain,
    emailDomain,
    jsqCustomerId,
    edition,
    timezone,
    userEmailId,
    userFirstName,
    userLastName,
    provisioningFlags,
    extensionFieldsFile,
    extensionEnumsFile,
    userRolesFile,
    userProvisioningFile,
    unitsFile,
    distributionListFile,
    documentCategoryFile,
    capitalTransactionTypesFile,
    distributionTypesFile,
    netIncomeBasesFile,
    netIncomeTypesFile,
    navComponentsFile,
    entityTypesFile,
  } = params;

  const tools: ToolConfiguration[] = [];

  const flags = provisioningFlags ? parseProvisioningFlags(provisioningFlags) : [];

  const provisionArenaOptions: Array<{ key: string; value: string }> = [
    { key: "name", value: arenaName },
    { key: "domain", value: domain },
    { key: "email_domain", value: emailDomain },
    // "0" is a valid, non-empty string here (truthy), not falsy
    ...(jsqCustomerId ? [{ key: "jsq_customer_id", value: jsqCustomerId }] : []),
    { key: "edition", value: edition.toLowerCase() },
    { key: "timezone", value: timezone },
  ];

  // Add provisioning flags if provided
  if (flags.length > 0) {
    provisionArenaOptions.push({
      key: "provisioning_flags",
      value: flags.join(","),
    });
  }

  if (extensionFieldsFile) {
    provisionArenaOptions.push({
      key: "extension_fields_file",
      value: `${domain}/${extensionFieldsFile.file.name}`,
    });
  }

  if (extensionEnumsFile) {
    provisionArenaOptions.push({
      key: "extension_enums_file",
      value: `${domain}/${extensionEnumsFile.file.name}`,
    });
  }

  tools.push({
    toolId: "provision_arena",
    toolType: "provision_arena",
    options: provisionArenaOptions,
  });

  tools.push({
    toolId: "provision_user_create",
    toolType: "provision_user_create",
    options: [
      { key: "arena", value: domain },
      { key: "email", value: userEmailId },
      { key: "first", value: userFirstName },
      { key: "last", value: userLastName },
      { key: "nopassword", value: "true" },
    ],
  });

  // Advanced Permissions configuration tools must run BEFORE provision_user_role
  // because provision_user_role depends on document categories and other config items existing
  if (unitsFile) {
    tools.push({
      toolId: "load_ownership_metrics",
      toolType: "load_ownership_metrics",
      options: [
        { key: "arena", value: domain },
        { key: "user", value: userEmailId },
        { key: "ownership_metric_file", value: `${domain}/${unitsFile.file.name}` },
      ],
    });
  }

  if (distributionListFile) {
    tools.push({
      toolId: "load_distribution_lists",
      toolType: "load_distribution_lists",
      options: [
        { key: "arena", value: domain },
        { key: "user", value: userEmailId },
        {
          key: "distribution_lists_file",
          value: `${domain}/${distributionListFile.file.name}`,
        },
      ],
    });
  }

  if (documentCategoryFile) {
    tools.push({
      toolId: "load_document_categories",
      toolType: "load_document_categories",
      options: [
        { key: "arena", value: domain },
        { key: "user", value: userEmailId },
        { key: "document_category_file", value: `${domain}/${documentCategoryFile.file.name}` },
      ],
    });
  }

  // Generate a single load_cash_flow_types command with all provided files
  // Each file parameter is optional - only include parameters for files that were uploaded
  if (
    capitalTransactionTypesFile ||
    distributionTypesFile ||
    netIncomeBasesFile ||
    netIncomeTypesFile ||
    navComponentsFile
  ) {
    const cashFlowOptions: Array<{ key: string; value: string }> = [
      { key: "arena", value: domain },
      { key: "user", value: userEmailId },
    ];

    if (capitalTransactionTypesFile) {
      cashFlowOptions.push({
        key: "transaction_type_file",
        value: `${domain}/${capitalTransactionTypesFile.file.name}`,
      });
    }

    if (distributionTypesFile) {
      cashFlowOptions.push({
        key: "distribution_type_file",
        value: `${domain}/${distributionTypesFile.file.name}`,
      });
    }

    if (netIncomeBasesFile) {
      cashFlowOptions.push({
        key: "net_income_basis_file",
        value: `${domain}/${netIncomeBasesFile.file.name}`,
      });
    }

    if (netIncomeTypesFile) {
      cashFlowOptions.push({
        key: "net_income_type_file",
        value: `${domain}/${netIncomeTypesFile.file.name}`,
      });
    }

    if (navComponentsFile) {
      cashFlowOptions.push({
        key: "nav_component_file",
        value: `${domain}/${navComponentsFile.file.name}`,
      });
    }

    tools.push({
      toolId: "load_cash_flow_types",
      toolType: "load_cash_flow_types",
      options: cashFlowOptions,
    });
  }

  if (entityTypesFile) {
    tools.push({
      toolId: "load_entity_types",
      toolType: "load_entity_types",
      options: [
        { key: "arena", value: domain },
        { key: "user", value: userEmailId },
        { key: "entity_type_file", value: `${domain}/${entityTypesFile.file.name}` },
      ],
    });
  }

  // User role provisioning must run AFTER Advanced Permissions config tools
  // because it depends on document categories and other config items existing
  if (userRolesFile) {
    tools.push({
      toolId: "provision_user_role",
      toolType: "provision_user_role",
      options: [
        { key: "arena", value: domain },
        { key: "created_by", value: userEmailId },
        { key: "roles_file", value: `${domain}/${userRolesFile.file.name}` },
      ],
    });
  }

  if (userProvisioningFile) {
    tools.push({
      toolId: "provision_bulk_user_role_create",
      toolType: "provision_user_update",
      options: [
        { key: "arena", value: domain },
        { key: "created_by", value: userEmailId },
        {
          key: "user_role_mapping_file",
          value: `${domain}/${userProvisioningFile.file.name}`,
        },
      ],
    });
  }

  return tools;
}
